/**
 * Pure helpers for the per-project Designations view (Phase 5).
 *
 * - Form validation for the create/edit dialog (shape invariants only;
 *   Firestore rules enforce WHO can write via `isProjectManager(projectId)
 *   || isFinance()`).
 * - Section ordering + grouping for the list view — sections render in
 *   a canonical order regardless of the order of Firestore writes, and
 *   designations inside a section are sorted alphabetically by `name`.
 *
 * Keep this file dependency-free (no React, no Firestore).
 */

import {
  DESIGNATION_SECTIONS,
  type BillingMode,
  type Designation,
  type DesignationSection,
} from '@altyor-kronos/shared-types';

// ─── Canonical section order ─────────────────────────────────────────

/**
 * The order in which sections are rendered in the list, regardless of
 * how designations were created. Mirrors the business convention
 * described in the spec (§13 Finance / Sections).
 *
 * We re-export `DESIGNATION_SECTIONS` unchanged as the canonical order,
 * because the shared-types enum already declares them in the intended
 * display order. Keeping a single source of truth avoids drift between
 * the type and the UI.
 */
export const SECTION_ORDER: readonly DesignationSection[] = DESIGNATION_SECTIONS;

/**
 * Natural order for designation names: `FW2` before `FW10`, `M2` before
 * `M11`. A plain `localeCompare` sorts digit by digit and puts sprint 10
 * right after sprint 1.
 */
export const compareDesignationNames = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }).compare;

// ─── Form values + validation ────────────────────────────────────────

export interface DesignationFormValues {
  section: DesignationSection;
  category: string;
  name: string;
  hoursSold: string;            // string form, parsed to number; ignored if billingMode === 'fixed'
  hourlyRateOverride: string;   // replaces hourlyRateId — empty = no override
  fixedAmount: string;          // string form, parsed to number; only used if billingMode === 'fixed'
  billingMode: BillingMode;
  catalogId: string | null;
  variant: string | null;
  isCustom: boolean;
}

export type FormErrors<T> = Partial<Record<keyof T, string>>;

/**
 * The Phase 5 dialog only surfaces the minimum set of fields the plan
 * calls for. All other Designation fields (monetary, computed) are
 * initialised to zero/false server-side in `DesignationsView`.
 *
 * Number fields are typed as strings in form state (MUI TextField), so
 * we parse them here and report errors symmetrically.
 */
export function validateDesignationForm(
  v: DesignationFormValues,
): FormErrors<DesignationFormValues> {
  const errors: FormErrors<DesignationFormValues> = {};
  if (!v.name || v.name.trim() === '') {
    errors.name = 'Name is required';
  }
  if (!v.section) {
    errors.section = 'Section is required';
  }
  if (v.billingMode !== 'hourly' && v.billingMode !== 'fixed') {
    errors.billingMode = 'Billing mode required';
  }
  if (v.billingMode === 'hourly') {
    const hoursSoldNum = Number(v.hoursSold);
    if (v.hoursSold === '' || !Number.isFinite(hoursSoldNum) || hoursSoldNum < 0) {
      errors.hoursSold = 'Hours sold must be a positive number';
    }
  }
  if (v.billingMode === 'fixed') {
    const fixedNum = Number(v.fixedAmount);
    if (v.fixedAmount === '' || !Number.isFinite(fixedNum) || fixedNum < 0) {
      errors.fixedAmount = 'Amount must be a positive number';
    }
  }
  return errors;
}

/**
 * Parse the validated form values into the concrete primitive types
 * consumed by Firestore writes. Call this only after
 * `validateDesignationForm` returned no errors.
 */
export function parseDesignationForm(v: DesignationFormValues): {
  section: DesignationSection;
  category: string | undefined;
  name: string;
  hoursSold: number | undefined;
  billingMode: BillingMode;
  hourlyRateOverride: number | undefined;
  initialOffer: number;
  catalogId: string | null;
  variant: string | null;
  isCustom: boolean;
} {
  const override =
    v.hourlyRateOverride.trim() === '' ? undefined : Number(v.hourlyRateOverride);
  return {
    section: v.section,
    category: v.category.trim() === '' ? undefined : v.category.trim(),
    name: v.name.trim(),
    billingMode: v.billingMode,
    hourlyRateOverride: Number.isFinite(override) && override! > 0 ? override : undefined,
    // hoursSold only set when billingMode === 'hourly':
    hoursSold: v.billingMode === 'hourly' ? Number(v.hoursSold) : undefined,
    // initialOffer = flat-fee amount when fixed, 0 when hourly (filled later in finance view):
    initialOffer: v.billingMode === 'fixed' ? Number(v.fixedAmount) : 0,
    catalogId: v.catalogId ?? null,
    variant: v.variant ?? null,
    isCustom: v.isCustom,
  };
}

// ─── Grouping + ordering ─────────────────────────────────────────────

export interface DesignationGroup {
  section: DesignationSection;
  items: Designation[];
}

/**
 * Group designations by section, preserving the canonical `SECTION_ORDER`
 * and sorting items inside each section alphabetically by `name`.
 *
 * Empty sections are omitted so the view doesn't show empty headers.
 */
export function groupDesignationsBySection(
  designations: readonly Designation[],
): DesignationGroup[] {
  const bySection = new Map<DesignationSection, Designation[]>();
  for (const d of designations) {
    const bucket = bySection.get(d.section);
    if (bucket) {
      bucket.push(d);
    } else {
      bySection.set(d.section, [d]);
    }
  }
  const groups: DesignationGroup[] = [];
  for (const section of SECTION_ORDER) {
    const items = bySection.get(section);
    if (!items || items.length === 0) continue;
    items.sort((a, b) => compareDesignationNames(a.name, b.name));
    groups.push({ section, items });
  }
  return groups;
}

// ─── Intra-project name uniqueness ───────────────────────────────────

/**
 * Normalise a designation name for uniqueness comparison: trim, collapse
 * internal whitespace, lowercase. Shared by the check + its tests so both
 * use exactly the same rules.
 *
 * We collapse whitespace because "HW1  Sprint" (double space, copy-paste
 * error) should collide with "HW1 Sprint" — Firestore would otherwise
 * happily store both.
 */
export function normalizeDesignationName(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Intra-project uniqueness check: is `candidate` free of conflict with
 * any designation already on this project?
 *
 * Firestore rules cannot enforce cross-document uniqueness (a rule sees
 * one doc at a time). We follow the same UI-layer pattern as
 * `isClientCodeUnique` / `isProjectCodeUnique`: check against the rows
 * already loaded via `onSnapshot`, with a tiny race window on concurrent
 * writes that is acceptable for low-frequency PM/finance work.
 *
 * `ignoreId` excludes the designation currently being edited so a rename
 * that doesn't change the name still validates.
 */
export function isDesignationNameUnique(
  candidate: string,
  existing: readonly { id: string; name: string }[],
  ignoreId?: string,
): boolean {
  const normalised = normalizeDesignationName(candidate);
  if (normalised === '') return true; // empty is handled by the required check
  return !existing.some(
    (d) => d.id !== ignoreId && normalizeDesignationName(d.name) === normalised,
  );
}

/**
 * Who is allowed to manage designations on this project?
 *
 * Mirrors branch A of `designationUpdate` in
 * `services/kronos-api/src/authz/policies/projectScoped.ts` — the
 * authoritative gate since the SDK was retired:
 *   isProjectManager(project) || isDirection() || isFinance() || isAdmin().
 *
 * `project_manager` and `rd_manager` are deliberately NOT here — the Entra
 * token never carries them, so granting on those shows a button whose
 * request 403s.
 */
export function canManageDesignations(
  userRoles: readonly string[],
  userUid: string,
  projectManagerUids: readonly string[],
): boolean {
  if (projectManagerUids.includes(userUid)) return true;
  return ['finance', 'direction', 'admin'].some((r) => userRoles.includes(r));
}

/**
 * Who can write the field-level subset of a designation — `progress` and
 * `isArchived`?
 *
 * Mirrors branch B of `designationUpdate` in
 * `services/kronos-api/src/authz/policies/projectScoped.ts`. Since admin
 * became a full editor (2026-09-24) this is the same set as
 * `canManageDesignations`; kept as its own name so callers read by intent.
 */
export function canWriteDesignationFields(
  userRoles: readonly string[],
  userUid: string,
  projectManagerUids: readonly string[],
): boolean {
  return canManageDesignations(userRoles, userUid, projectManagerUids);
}
